// src/pages/DoorloopjeDetail.js

import React, { useEffect, useMemo, useState, useId, useRef } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import { useDoorloopjes } from '../context/DoorloopjesContext';
import { BlocksRenderer } from '@strapi/blocks-react-renderer';
import { trackEvent } from '../utils/track';
import StapKaart from '../components/StapKaart';

import { ReactComponent as ChevronUpIcon } from '../assets/icon-chevron-up.svg';
import { ReactComponent as ChevronDownIcon } from '../assets/icon-chevron-down.svg';
import { ReactComponent as DownloadIcon } from '../assets/icon-download.svg';
import { chatVraag } from '../utils/chatVragen';
import { bewaarDoorloopjePdf } from '../utils/doorloopjePdf';

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL;

// Helper: proxify image URLs to avoid CORS issues
const proxify = (url) =>
  `${process.env.REACT_APP_STRAPI_URL}/api/media-proxy?url=${encodeURIComponent(url)}`;

// Helper: haal ID + (optionele) privacy hash uit een Vimeo URL
function getVimeoIdAndHash(url) {
  if (!url) return {};
  const match = /vimeo\.com\/(\d+)(?:\/([a-z0-9]+))?/i.exec(url);
  return match ? { id: match[1], hash: match[2] || null } : {};
}

/**
 * Compacte SVG-badge. Klik op de PLAY (ruime hit-area) opent de video (zelfde modal als knop).
 * - Toont optioneel 'thumbnailUrl' binnen het "scherm".
 */
const VideoBadge = ({ onClick, thumbnailUrl }) => {
  const clipId = useId();

  const ScreenImage = () => (
    <>
      <rect x="30" y="30" width="240" height="140" fill="#fff" pointerEvents="none" />
      {thumbnailUrl ? (
        <>
          <defs>
            <clipPath id={clipId}>
              <rect x="30" y="30" width="240" height="140" rx="0" ry="0" />
            </clipPath>
          </defs>
          <image
            href={thumbnailUrl}
            x="30"
            y="30"
            width="240"
            height="140"
            preserveAspectRatio="xMidYMid slice"
            clipPath={`url(#${clipId})`}
            style={{ imageRendering: 'auto' }}
          />
        </>
      ) : null}
    </>
  );

  return (
    <div aria-hidden="false" style={{ width: 260 }}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 300 240"
        width="320"
        height="256"
        role="img"
        aria-label="Video afspelen"
      >
        <rect x="0" y="0" width="300" height="240" rx="20" fill="#ffd300" pointerEvents="none" />
        <rect x="20" y="20" width="260" height="200" fill="#000" rx="4" pointerEvents="none" />
        <ScreenImage />
        <g
          transform="translate(150,100)"
          onClick={onClick}
          role="button"
          tabIndex="0"
          aria-label="Speel video"
          style={{ cursor: 'pointer' }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick();
            }
          }}
        >
          <circle r="28" fill="transparent" stroke="none" />
          <circle r="24" fill="none" stroke="#000" strokeWidth="2" />
          <path d="M-8,-12 L14,0 L-8,12 Z" fill="#000" />
        </g>
        <rect x="30" y="180" width="240" height="30" fill="#000" pointerEvents="none" />
        <g transform="translate(50,195)" pointerEvents="none">
          <path d="M -5,-6 L 5,0 L -5,6 Z" fill="#fff" />
        </g>
        <g transform="translate(100,195)" pointerEvents="none">
          <rect x="0" y="-1" width="140" height="2" fill="#e6e6e6" opacity="0.9" />
          <rect x="0" y="-1" width="58" height="2" fill="#ffffff" />
          <circle cx="58" cy="0" r="4" fill="#ffffff" stroke="#000" strokeWidth="0.5" />
        </g>
      </svg>
    </div>
  );
};

function DoorloopjeDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { doorloopjes, loading, currentUser, toggleFavorite, setCompletionState, globalSettings } = useDoorloopjes();

  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfWithNotes, setPdfWithNotes] = useState(false);

  const pdfContentRef = useRef(null);

  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 768px)').matches : false
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mm = window.matchMedia('(max-width: 768px)');
    const handler = (e) => setIsMobile(e.matches);
    if (mm.addEventListener) mm.addEventListener('change', handler);
    else mm.addListener(handler);
    return () => {
      if (mm.removeEventListener) mm.removeEventListener('change', handler);
      else mm.removeListener(handler);
    };
  }, []);

  const doorloopje = useMemo(
    () => doorloopjes.find((d) => d.slug === slug),
    [doorloopjes, slug]
  );

  // Fetch steps + digidates separately (not included in bulk context query for performance)
  const [detailData, setDetailData] = useState({ steps: [], digidates: [], downloads: [] });
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    // Met de login: doorloopjes alleen voor videocoaching geeft de backend anders niet terug.
    axios.get(
      `${STRAPI_URL}/api/doorloopjes?filters[slug][$eq]=${encodeURIComponent(slug)}` +
      `&populate[steps][populate]=image&populate[digidates][populate]=image&populate[downloads]=true&fields[0]=id`,
      { headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` } }
    ).then(res => {
      if (!cancelled) {
        const item = res.data?.data?.[0];
        setDetailData({
          steps: item?.steps || [],
          digidates: item?.digidates || [],
          downloads: item?.downloads || [],
        });
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [slug]);

  const { id: vimeoId, hash: vimeoHash } = useMemo(
    () => getVimeoIdAndHash(doorloopje?.vimeo_url),
    [doorloopje?.vimeo_url]
  );

  const [thumbnailUrl, setThumbnailUrl] = useState('');
  useEffect(() => {
    let abort = false;
    async function loadThumb() {
      if (!vimeoId) {
        if (!abort) setThumbnailUrl('');
        return;
      }
      try {
        const vimeoPublicUrl = `https://vimeo.com/${vimeoId}${vimeoHash ? '/' + vimeoHash : ''}`;
        const oembedUrl = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(vimeoPublicUrl)}`;
        const resp = await fetch(oembedUrl, { mode: 'cors' });
        if (!resp.ok) throw new Error(`oEmbed HTTP ${resp.status}`);
        const data = await resp.json();
        if (!abort) {
          const thumb = typeof data.thumbnail_url === 'string'
            ? data.thumbnail_url.replace(/_\d+x\d+\.jpg$/, '_640.jpg')
            : '';
          setThumbnailUrl(thumb || data.thumbnail_url || '');
        }
      } catch {
        if (!abort) setThumbnailUrl('');
      }
    }
    loadThumb();
    return () => { abort = true; };
  }, [vimeoId, vimeoHash]);

  const activeState = useMemo(() => {
    if (!currentUser || !doorloopje) return 'not_started';
    const isInProgress = currentUser.inprogress_doorloopjes?.some((d) => d.id === doorloopje.id);
    const isCompleted = currentUser.completed_doorloopjes?.some((d) => d.id === doorloopje.id);
    if (isCompleted) return 'completed';
    if (isInProgress) return 'in_progress';
    return 'not_started';
  }, [currentUser, doorloopje]);

  const isFavorite = currentUser?.favorite_doorloopjes.some((fav) => fav.id === doorloopje?.id);

  const handleToggleFavorite = () => {
    if (!doorloopje) return;
    toggleFavorite(doorloopje.id);
  };

  const handleStateChange = (newState) => {
    if (!doorloopje) return;
    setCompletionState(doorloopje.id, newState);
  };

  const handleVideoClick = () => {
    setIsVideoModalOpen(true);
    trackEvent({
      type: 'video_play',
      path: window.location.pathname + window.location.search,
      title: document.title,
      payload: {
        doorloopje_id: doorloopje?.id,
        doorloopje_title: doorloopje?.title,
        vimeo_id: vimeoId,
        device: isMobile ? 'mobile' : 'desktop'
      }
    });
  };

  const handleAiAction = (actionName) => {
    if (!doorloopje) return;

    // Check of de SDK beschikbaar is
    if (!window.PAP_Chat || typeof window.PAP_Chat.open !== 'function') {
      console.warn('PAP_Chat SDK niet geladen of beschikbaar.');
      return;
    }

    // Bepaal de gewenste status (maximized op mobiel, normaal op desktop)
    const targetState = isMobile ? 'maximized' : 'normal';

    const prompt = chatVraag(actionName, doorloopje.title);
    if (prompt) {
      // Open de chat en vul de input
      window.PAP_Chat.open({ message: prompt, state: targetState });
      return;
    }

    if (actionName === 'Stel vraag') {
      // Gewoon openen zonder bericht in te vullen
      window.PAP_Chat.open({ state: targetState });
      return;
    }
  };

  const handleDownloadPDF = () => {
    setShowPdfModal(true);
  };

  const generatePDF = async (withNotes = false) => {
    if (!doorloopje || !pdfContentRef.current) return;

    setShowPdfModal(false);
    setPdfWithNotes(withNotes);
    setIsGeneratingPDF(true);

    try {
      // Wait for content to be visible
      await new Promise(resolve => setTimeout(resolve, 500));

      // Generate filename from doorloopje title
      const filename = `Doorloopje - ${doorloopje.title.replace(/[^a-z0-9]/gi, '_')}.pdf`;
      await bewaarDoorloopjePdf(pdfContentRef.current, filename);

      trackEvent({
        type: 'pdf_download',
        path: window.location.pathname + window.location.search,
        title: document.title,
        payload: {
          doorloopje_id: doorloopje.id,
          doorloopje_title: doorloopje.title,
          with_notes: withNotes,
        }
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('De pdf maken lukte niet. Probeer het opnieuw.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  if (loading) return <p>Doorloopje wordt geladen...</p>;
  if (!doorloopje) return <p>Doorloopje niet gevonden...</p>;

  const currentIndex = doorloopjes.findIndex((d) => d.id === doorloopje.id);
  const prevDoorloopje = doorloopjes[currentIndex - 1];
  const nextDoorloopje = doorloopjes[currentIndex + 1];

  const isLongDescription = doorloopje.description?.length > 1;

  return (
    <div className="detail-page-container">
      <button className="back-button" onClick={() => navigate(-1)}>
        <ChevronUpIcon className="back-icon" />
        <span>Terug</span>
      </button>
      <header className="detail-page-header">
        <div className="header-title-block">
          <p className="detail-breadcrumb">{doorloopje.theme?.title?.toUpperCase() || 'ALGEMEEN'}</p>
          <h1 className="detail-title">{doorloopje.title}</h1>
        </div>
        <div className="detail-actions-top">
          <button className={`btn-icon btn-save ${isFavorite ? 'active' : ''}`} onClick={handleToggleFavorite}>
            <span className="btn-save-icon">♥</span>
            {isFavorite ? 'OPGESLAGEN' : 'OPSLAAN'}
          </button>

          <div className="completion-status-picker">
            <div
              className={`status-option ${activeState === 'not_started' ? 'active not-started' : ''}`}
              onClick={() => handleStateChange('not_started')}
            >
              Nog niet begonnen
            </div>
            <div
              className={`status-option ${activeState === 'in_progress' ? 'active in-progress' : ''}`}
              onClick={() => handleStateChange('in_progress')}
            >
              Bezig
            </div>
            <div
              className={`status-option ${activeState === 'completed' ? 'active completed' : ''}`}
              onClick={() => handleStateChange('completed')}
            >
              Afgerond
            </div>
          </div>

          <div className="nav-arrows">
            {prevDoorloopje ? (
              <RouterLink to={`/doorloopjes/${prevDoorloopje.slug}`} className="arrow-button">
                <ChevronUpIcon />
              </RouterLink>
            ) : (
              <span className="arrow-button disabled">
                <ChevronUpIcon />
              </span>
            )}
            {nextDoorloopje ? (
              <RouterLink to={`/doorloopjes/${nextDoorloopje.slug}`} className="arrow-button">
                <ChevronDownIcon />
              </RouterLink>
            ) : (
              <span className="arrow-button disabled">
                <ChevronDownIcon />
              </span>
            )}
          </div>
        </div>
      </header>

      {/* PDF Export Container (hidden, only used for PDF generation) */}
      <div className={`pdf-export-container ${isGeneratingPDF ? 'generating' : ''} ${pdfWithNotes ? 'with-notes' : ''}`} ref={pdfContentRef}>
        <div className="pdf-header">
          <div className="pdf-title-block">
            <p className="pdf-theme">{doorloopje.theme?.title?.toUpperCase() || 'ALGEMEEN'}</p>
            <h1 className="pdf-title">{doorloopje.title}</h1>
            {doorloopje.book && <p className="pdf-book-info">Boek {doorloopje.book}, Pagina {doorloopje.page}</p>}
          </div>
          {globalSettings?.logo?.url && (
            <img
              src={proxify(globalSettings.logo.url)}
              alt="Doorloopjes"
              className="pdf-logo"
              crossOrigin="anonymous"
            />
          )}
        </div>

        {doorloopje.description && (
          <div className="pdf-description">
            <BlocksRenderer content={doorloopje.description} />
          </div>
        )}

        <div className="pdf-steps-wrapper">
          <div className="pdf-steps">
            {detailData.steps &&
              detailData.steps.map((step, index) => {
                const imageUrl = step.image?.url || null;
                return (
                  <div key={step.id} className="pdf-step">
                    {imageUrl && (
                      <div className="pdf-step-image-container">
                        <img
                          src={proxify(imageUrl)}
                          alt={`Stap ${index + 1}`}
                          className="pdf-step-image"
                          crossOrigin="anonymous"
                        />
                      </div>
                    )}
                    <div className="pdf-step-content">
                      <div className="pdf-step-header">
                        <span className="pdf-step-number">{index + 1}</span>
                        {step.title && <h3 className="pdf-step-title">{step.title}</h3>}
                      </div>
                      {step.description && (
                        <div className="pdf-step-description">
                          <BlocksRenderer content={step.description} />
                        </div>
                      )}
                    </div>
                    {pdfWithNotes && (
                      <div className="pdf-notes-column">
                        {Array.from({ length: 6 }, (_, regel) => (
                          <div key={regel} className="pdf-note-line"></div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>

        {!pdfWithNotes && (
          <div className="pdf-footer">
            <p>Gegenereerd via doorloopjes.nl</p>
          </div>
        )}
      </div>

      <main className="detail-main-content">
        <div className="book-info-row">
          {doorloopje.book && (
            <div className="book-info">
              Boek {doorloopje.book}, Pagina {doorloopje.page}
            </div>
          )}
          <button
            className="btn-icon btn-download"
            onClick={handleDownloadPDF}
            disabled={isGeneratingPDF}
          >
            <DownloadIcon className="btn-download-icon" />
            {isGeneratingPDF ? 'GENEREREN...' : 'DOWNLOAD PDF'}
          </button>
        </div>

        <div
          className={`description-wrapper ${!isDescriptionExpanded && isLongDescription ? 'collapsed' : ''} description-with-video`}
        >
          {doorloopje.description && (
            <div className="detail-description">
              <BlocksRenderer content={doorloopje.description} />
            </div>
          )}
          {vimeoId && !isMobile && (
            <div className="video-badge-wrapper">
              <VideoBadge
                thumbnailUrl={thumbnailUrl}
                onClick={handleVideoClick}
              />
            </div>
          )}
        </div>

        {isLongDescription && !isDescriptionExpanded && (
          <button onClick={setIsDescriptionExpanded.bind(null, true)} className="btn-expand-description">
            Meer...
          </button>
        )}

        {vimeoId && isMobile && (
          <button
            className="btn-icon btn-video"
            onClick={handleVideoClick}
          >
            BEKIJK VIDEO
          </button>
        )}

        <div className="steps-grid">
          {detailData.steps &&
            detailData.steps.map((step, index) => (
              <StapKaart
                key={step.id}
                nummer={index + 1}
                titel={step.title}
                afbeelding={step.image?.url || null}
                alt={`Stap ${index + 1}`}
              >
                {step.description && <BlocksRenderer content={step.description} />}
              </StapKaart>
            ))}

          <div className="actions-card-wrapper">
            <div className="actions-card">
              <h4>Acties & AI Follow-Ups</h4>
              <button className="action-button" onClick={() => handleAiAction('Maak lesplan')}>
                Maak een lesplan met AI
              </button>
              <button className="action-button primary" onClick={() => handleAiAction('Stel vraag')}>
                Stel een vraag
              </button>
              <button className="action-button" onClick={() => handleAiAction('Maak een samenvatting')}>
                Maak een samenvatting
              </button>
            </div>
          </div>
        </div>

        {/* ===== Downloads – bijv. een printbare praktijkkaart ===== */}
        {Array.isArray(detailData.downloads) && detailData.downloads.length > 0 && (
          <>
            <h3 className="related-title">Download</h3>
            <div className="downloads-list">
              {detailData.downloads.map((file) => (
                <a
                  key={file.id}
                  href={file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="download-link"
                  onClick={() =>
                    trackEvent({
                      type: 'pdf_download',
                      path: window.location.pathname + window.location.search,
                      title: document.title,
                      payload: {
                        content_title: `${doorloopje.title} - ${file.name}`,
                        doorloopje_id: doorloopje.id,
                        doorloopje_title: doorloopje.title,
                        file_name: file.name,
                        bron: 'doorloopje-detail',
                      },
                    })
                  }
                >
                  {file.caption || file.name}
                </a>
              ))}
            </div>
          </>
        )}

        {/* ===== "Behandeld in" – lijst met gekoppelde webinars (digidates) ===== */}
        {Array.isArray(detailData.digidates) && detailData.digidates.length > 0 && (
          <>
            <h3 className="related-title">Behandeld in</h3>
            <div className="related-doorloopjes-list">
              {detailData.digidates.map((w) => {
                const img =
                  w.image?.formats?.thumbnail?.url ||
                  w.image?.url ||
                  null;
                const dateStr = w.event_date
                  ? new Date(w.event_date).toLocaleDateString('nl-NL', {
                      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
                    })
                  : null;

                return (
                  <RouterLink
                    to={`/webinars/${w.slug}`}
                    key={w.id}
                    className="doorloopje-card"
                    title={w.title}
                  >
                    <div className="card-icon">
                      {img ? <img src={img} alt={w.title} /> : <div className="placeholder" />}
                    </div>
                    <div className="card-text">
                      <h3>{w.title}</h3>
                      {dateStr && (
                        <p>
                          {w.has_taken_place ? 'Opname van' : 'Aankomend op'} {dateStr}
                        </p>
                      )}
                      {!w.has_taken_place && (
                        <p className="signup-prompt"><strong>Meld je aan</strong></p>
                      )}
                    </div>
                    <div className="card-arrow">{'>'}</div>
                  </RouterLink>
                );
              })}
            </div>
          </>
        )}
      </main>

      {showPdfModal && (
        <div className="modal-backdrop show" onClick={() => setShowPdfModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Kies PDF versie</h2>
              <button className="modal-close-btn" onClick={() => setShowPdfModal(false)}>
                ×
              </button>
            </div>
            <p style={{ marginBottom: '1.5rem', color: 'var(--color-dark-grey)' }}>
              Hoe wil je de PDF downloaden?
            </p>
            <div className="pdf-modal-options">
              <button
                className="pdf-option-btn"
                onClick={() => generatePDF(false)}
              >
                <div className="pdf-option-icon">📄</div>
                <div className="pdf-option-text">
                  <strong>Standaard PDF</strong>
                  <span>Alleen de doorloopje informatie</span>
                </div>
              </button>
              <button
                className="pdf-option-btn"
                onClick={() => generatePDF(true)}
              >
                <div className="pdf-option-icon">✏️</div>
                <div className="pdf-option-text">
                  <strong>Met aantekeningenruimte</strong>
                  <span>Inclusief lijntjes om aantekeningen te maken</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {isVideoModalOpen && vimeoId && (
        <div className="video-modal-backdrop" onClick={() => setIsVideoModalOpen(false)}>
          <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="video-modal-close-btn" onClick={() => setIsVideoModalOpen(false)}>
              ×
            </button>
            <div className="video-player-wrapper">
              <iframe
                src={`https://player.vimeo.com/video/${vimeoId}${
                  vimeoHash ? `?h=${vimeoHash}&` : '?'
                }autoplay=1&color=ffdd00&title=0&byline=0&portrait=0`}
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                title={doorloopje.title}
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DoorloopjeDetail;